import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Loader2, RefreshCw, AlertTriangle, CheckCircle2, ArrowRight, Users, Send,
  Megaphone, Star, PackageOpen, CalendarDays, Activity, Home, CalendarX, X, PauseCircle,
} from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import { BADGE_FONT_PX, ON_CARD, tokensFor } from './cadenceBadges';

const API = '/api';

const ymd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// The "This week" panel shows now..now+7d; the calendar window asked for is a day wider on
// each side (`to` is exclusive), so a stored time with a timezone offset cannot fall outside.
export function dashWindow(now = new Date()) {
  const day = (n) => new Date(now.getFullYear(), now.getMonth(), now.getDate() + n);
  return { from: ymd(day(-1)), to: ymd(day(9)) };
}

// Hub Dashboard — the default landing screen.
//   1. NEEDS YOUR ATTENTION — same source of truth as the daily digest email, in three zones:
//      the verdict slot (empty until Stage 2), "Do now" (at most three rows, chosen by the
//      backend: dashboard_api.zone) and "Everything else" (one collapsed line).
//      Every row says what it is in one sentence, carries ONE action — performed here where
//      the Hub can, or a link to the exact screen with the reason it can't — and can be
//      dismissed: not now (back tomorrow), remind me in 30 days, or not ever.
//   2. THE MACHINE AT A GLANCE — tiles, every number deep-links to where you act.

function fmtWhen(s) {
  if (!s) return '';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function Tile({ title, icon: Icon, onClick, children, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className="text-left bg-hg-surface border border-hg-border rounded-sm p-4 hover:border-hg-green/60 transition-colors w-full">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim inline-flex items-center gap-1.5">
          <Icon size={12} className="text-hg-green" /> {title}
        </div>
        <ArrowRight size={12} className="text-hg-text-dim/50" />
      </div>
      {children}
    </button>
  );
}

// A MISSED cadence occurrence is the one card that must NOT quietly age out. Everything
// else in this zone disappears when its query stops matching, which is correct for work
// that got done — but a date that passed unsent doesn't stop being true just because the
// date recedes. It clears when Randall dismisses it or the slot gets filled, nothing else.
// Colours come from cadenceBadges as SOLID inline styles, not Tailwind alpha utilities.
// The first version used `bg-red-500/25 text-red-200`, which on this light theme composited
// to 1.10:1 for MISSED and 1.12:1 for THIN at 9px — the element stating how bad the problem
// is was the least readable thing on the card. Alpha was the root cause: a translucent
// chip's real colour depends on whatever it sits on, so it cannot be checked by arithmetic.
// Every pairing used here is asserted >= 4.5:1 in cadenceBadges.contrast.test.js.
function CadenceCard({ it, onOpen, onDismiss, busy }) {
  const cad = it.cadence || {};
  const { badge, cardBg, cardBorder } = tokensFor(cad.verdict);
  return (
    <div
      data-testid={`attention-cadence-${cad.commitment_key}`}
      data-verdict={cad.verdict}
      className="w-full rounded-sm border transition-colors"
      style={{ backgroundColor: cardBg, borderColor: cardBorder }}
    >
      <div className="flex items-center gap-3 px-3 py-2.5">
      <span className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-sm"
        style={{ backgroundColor: badge.bg, color: badge.icon }}>
        <CalendarX size={15} />
      </span>
      <button onClick={onOpen} className="flex-1 min-w-0 text-left">
        <span className="flex items-center gap-2">
          <span
            data-testid={`cadence-badge-${cad.commitment_key}`}
            className="font-sans font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm shrink-0"
            style={{ backgroundColor: badge.bg, color: badge.fg,
                     fontSize: `${BADGE_FONT_PX}px`, lineHeight: 1.4 }}
          >{cad.verdict}</span>
          <span className="text-[13px] font-sans text-hg-text truncate">{it.title}</span>
        </span>
        {it.subtitle && (
          <span className="block text-[11px] font-sans text-hg-text-dim truncate mt-0.5">{it.subtitle}</span>
        )}
      </button>
      {(cad.dismissable || []).length > 0 && (
        <button
          onClick={() => onDismiss(cad.commitment_key, cad.dismissable[0])}
          disabled={busy}
          title={`Dismiss the ${cad.dismissable[0]} miss — it won't come back`}
          data-testid={`cadence-dismiss-${cad.commitment_key}`}
          className="shrink-0 inline-flex items-center gap-1 text-[10px] font-sans border border-hg-border rounded-sm px-1.5 py-1 disabled:opacity-40"
          style={{ color: ON_CARD.lapsed }}
        >
          {busy ? <Loader2 size={11} className="animate-spin" /> : <X size={11} />} Dismiss
        </button>
      )}
      <ArrowRight size={13} className="shrink-0 text-hg-text-dim/60" />
      </div>
      <HoldRow held={cad.held} lapsed={cad.lapsed} />
    </div>
  );
}

// Held slots live BELOW the card, deliberately neutral — no red, no amber. A slot that is
// empty because someone decided it should be is not a warning, and painting it like one is
// how a card stops being read. The reason and the lapse date are both on the face of it:
// a hold you cannot see the reason for is just a mute with extra steps.
function HoldRow({ held, lapsed }) {
  if (!held?.length && !lapsed?.length) return null;
  return (
    <div className="pl-11 pr-3 pb-2 space-y-1" data-testid="cadence-holds">
      {(held || []).map((h) => (
        <div key={h.date} data-testid={`cadence-held-${h.date}`}
          className="flex items-start gap-2 text-[11px] font-sans"
          style={{ color: ON_CARD.hold }}>
          <PauseCircle size={11} className="mt-0.5 shrink-0" />
          <span className="min-w-0">
            <span className="font-medium">{h.label} held</span>
            {h.reason && <span> — {h.reason}</span>}
            {h.expires_at && (
              <span style={{ color: ON_CARD.holdMeta }}>
                {' · '}lapses {h.expires_at}
                {typeof h.lapses_in_days === 'number' && ` (${h.lapses_in_days}d)`}
              </span>
            )}
          </span>
        </div>
      ))}
      {(lapsed || []).map((l) => (
        <div key={l.date} data-testid={`cadence-lapsed-${l.date}`}
          className="flex items-start gap-2 text-[11px] font-sans"
          style={{ color: ON_CARD.lapsed }}>
          <PauseCircle size={11} className="mt-0.5 shrink-0" />
          <span className="min-w-0">
            <span className="font-medium">{l.label} hold lapsed</span>
            {l.expired_at && <span style={{ color: ON_CARD.holdMeta }}>{' · '}expired {l.expired_at}</span>}
            {l.reason && <span style={{ color: ON_CARD.holdMeta }}> — was: {l.reason}</span>}
          </span>
        </div>
      ))}
    </div>
  );
}

// Three THIN cadence cards used to say one fact three times. The backend folds them into one
// row (dashboard_api.collapse_thin_cadence); the row opens the EARLIEST commitment, and each
// commitment named on it is its own link to where that card used to go.
function ThinCadenceRow({ it, go }) {
  return (
    <div data-testid="attention-cadence-thin"
      className="w-full flex items-center gap-3 bg-hg-surface border border-hg-border rounded-sm px-3 py-2.5">
      <span className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-sm bg-hg-bg text-hg-text-dim">
        <CalendarX size={15} />
      </span>
      <span className="flex-1 min-w-0">
        <button onClick={() => go(it.module, { tab: it.tab })}
          className="block text-left text-[13px] font-sans text-hg-text">Content calendar thin</button>
        <span className="flex flex-wrap gap-1.5 mt-1">
          {(it.parts || []).map((p) => (
            <button key={p.key} onClick={() => go(p.module, { tab: p.tab })}
              data-testid={`attention-thin-${p.key}`}
              className="text-[11px] font-sans text-hg-text border border-hg-border rounded-sm px-1.5 py-0.5 hover:border-hg-green">
              {p.label} · next empty {p.next_empty_label}
            </button>
          ))}
        </span>
      </span>
      {typeof it.due_in_days === 'number' && (
        <span className="shrink-0 text-[10px] font-sans tabular-nums px-1.5 py-0.5 rounded-sm bg-hg-bg text-hg-text-dim">
          {it.due_in_days === 0 ? 'today' : `in ${it.due_in_days}d`}
        </span>
      )}
      <ArrowRight size={13} className="shrink-0 text-hg-text-dim/60" />
    </div>
  );
}

// The three ways to put a row away. "Not ever" hides THIS row as it stands: a per-person row
// (Onboard David Hobson) is gone for good; a count row comes back only when something new
// joins it (attention_dismissals.py says why).
const DISMISS_MODES = [
  { mode: 'not_now', label: 'Not now', hint: 'back tomorrow' },
  { mode: 'remind_30', label: 'Remind me in 30 days', hint: 'back in 30 days' },
  { mode: 'not_ever', label: 'Not ever', hint: 'gone for good' },
];

function DismissMenu({ it, onDismiss, busy }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative shrink-0">
      <button onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }} disabled={busy}
        title="Dismiss" aria-label={`Dismiss: ${it.title}`} aria-expanded={open}
        data-testid={`dismiss-open-${it.key}`}
        className="inline-flex items-center justify-center w-7 h-7 rounded-sm border border-hg-border text-hg-text-dim hover:text-hg-text disabled:opacity-40">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />}
      </button>
      {open && (
        <span role="menu" className="absolute right-0 top-8 z-20 w-52 bg-hg-surface border border-hg-border rounded-sm shadow-lg py-1">
          {DISMISS_MODES.map((d) => (
            <button key={d.mode} role="menuitem" data-testid={`dismiss-${d.mode}-${it.key}`}
              onClick={(e) => { e.stopPropagation(); setOpen(false); onDismiss(it, d.mode); }}
              className="w-full text-left px-3 py-1.5 text-[12px] font-sans text-hg-text hover:bg-hg-bg">
              {d.label} <span className="text-hg-text-dim">· {d.hint}</span>
            </button>
          ))}
        </span>
      )}
    </span>
  );
}

// ONE action per row. `perform` runs it here (the backend only offers ones that send, post,
// approve, schedule and draft nothing); `link` opens the exact screen and says why it can't
// be done from the dashboard, so a link never reads as a button that forgot to work.
function ActionButton({ it, go, onPerform, busy }) {
  const a = it.action;
  if (!a) return null;
  if (a.kind === 'perform') {
    return (
      <button onClick={() => onPerform(it)} disabled={busy} data-testid={`action-${it.key}`}
        className="shrink-0 text-[11px] font-sans font-medium px-2 py-1 rounded-sm bg-hg-green text-white disabled:opacity-40">
        {busy ? <Loader2 size={11} className="animate-spin inline" /> : a.label}
      </button>
    );
  }
  return (
    <button onClick={() => go(a.module, a.params || {})} data-testid={`action-${it.key}`}
      title={a.why || ''}
      className="shrink-0 inline-flex items-center gap-1 text-[11px] font-sans px-2 py-1 rounded-sm border border-hg-border text-hg-text hover:border-hg-green">
      {a.label} <ArrowRight size={11} />
    </button>
  );
}

function AttentionRow({ it, go, onDismiss, onPerform, busy, done }) {
  const high = it.severity === 'high';
  return (
    <div data-testid={it.key === 'social_drafts' ? 'attention-social-drafts' : `attention-${it.type}`}
      data-key={it.key}
      className={`w-full bg-hg-surface border rounded-sm px-3 py-2.5 ${high ? 'border-amber-400/60' : 'border-hg-border'}`}>
      <div className="flex items-start gap-3">
        <span className="flex-1 min-w-0">
          <span className="block text-[13px] font-sans text-hg-text">
            {(it.key === 'social_drafts' || String(it.key || '').startsWith('social_watchdog')) && (
              <span data-testid="attention-draft-badge"
                className="mr-1.5 align-middle text-[9px] font-sans font-bold uppercase tracking-wider text-hg-green border border-hg-green/40 rounded-sm px-1 py-0.5">
                Draft
              </span>
            )}
            {it.sentence || it.title}
          </span>
          {(it.new_since_dismissed || []).length > 0 && (
            <span className="block text-[11px] font-sans text-amber-300 mt-0.5" data-testid={`attention-new-${it.key}`}>
              Back because {it.new_since_dismissed.length} new since you dismissed it
            </span>
          )}
          {it.action?.kind === 'link' && it.action.why && (
            <span className="block text-[11px] font-sans text-hg-text-dim mt-0.5" data-testid={`attention-why-${it.key}`}>
              {it.action.why}
            </span>
          )}
          {done && <span className="block text-[11px] font-sans text-hg-green mt-0.5">{done}</span>}
        </span>
        {typeof it.due_in_days === 'number' && (
          <span data-testid={`attention-due-${it.key}`}
            className={`shrink-0 text-[10px] font-sans tabular-nums px-1.5 py-0.5 rounded-sm mt-0.5 ${
              it.due_in_days < 0 ? 'bg-amber-400/15 text-amber-300' : 'bg-hg-bg text-hg-text-dim'}`}>
            {it.due_in_days < 0 ? `${Math.abs(it.due_in_days)}d overdue`
              : it.due_in_days === 0 ? 'today' : `in ${it.due_in_days}d`}
          </span>
        )}
        <ActionButton it={it} go={go} onPerform={onPerform} busy={busy} />
        <DismissMenu it={it} onDismiss={onDismiss} busy={busy} />
      </div>
    </div>
  );
}

export default function Dashboard({ onNavigate }) {
  // Who did it, for actions that record an actor (as each module's own button sends).
  const signedIn = useAuth()?.user?.email || null;
  const [data, setData] = useState(null);
  const [cal, setCal] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dismissing, setDismissing] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [d, c] = await Promise.allSettled([
        axios.get(`${API}/dashboard`),
        axios.get(`${API}/calendar/events`, { params: dashWindow() })]);
      if (d.status === 'fulfilled') setData(d.value.data);
      else setError(d.reason?.response?.data?.detail || 'Could not load the dashboard.');
      if (c.status === 'fulfilled') {
        const now = new Date(); const end = new Date(now.getTime() + 7 * 864e5);
        const evs = (c.value.data?.events || c.value.data || [])
          .filter((e) => { const t = new Date(e.date); return t >= now && t <= end; })
          .sort((a, b) => new Date(a.date) - new Date(b.date));
        setCal(evs.slice(0, 8));
      }
    } catch (e) { setError('Could not load the dashboard.'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Deep-link: set URL params (so target screens can read view/tab at mount),
  // then switch modules in-app.
  // Any other param an action carries (a record to open) rides along in the URL too.
  const go = (module, opts = {}) => {
    try {
      const p = new URLSearchParams();
      p.set('module', module);
      Object.entries(opts).forEach(([k, v]) => { if (v != null && v !== '') p.set(k, String(v)); });
      window.history.replaceState(null, '', `?${p.toString()}`);
    } catch { /* URL update is best-effort */ }
    onNavigate?.(module, opts.tab ? { tab: opts.tab } : undefined);
  };

  const [rowBusy, setRowBusy] = useState('');
  const [doneMsg, setDoneMsg] = useState({});
  const [showRest, setShowRest] = useState(false);
  const [dismissed, setDismissed] = useState(null);

  const dismissItem = async (it, mode) => {
    setRowBusy(it.key);
    try {
      await axios.post(`${API}/dashboard/dismiss`, { key: it.key, mode });
      await load();
      if (dismissed) setDismissed(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not dismiss that.');
    }
    setRowBusy('');
  };

  const performItem = async (it) => {
    const a = it.action || {};
    if (a.kind !== 'perform' || !String(a.path || '').startsWith('/api/')) return;
    setRowBusy(it.key);
    try {
      // `actor: null` in a body means "whoever is signed in", as each module's own button sends.
      const body = { ...(a.body || {}) };
      if ('actor' in body && body.actor == null) body.actor = signedIn || 'admin';
      await axios({ method: a.method || 'post', url: a.path, data: body });
      setDoneMsg((m) => ({ ...m, [it.key]: a.done || 'Done.' }));
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || `Could not ${String(a.label || 'do that').toLowerCase()}.`);
    }
    setRowBusy('');
  };

  const showDismissed = async () => {
    try { setDismissed((await axios.get(`${API}/dashboard/dismissed`)).data?.dismissed || []); }
    catch { setError('Could not load the dismissed list.'); }
  };

  const bringBack = async (key) => {
    try {
      await axios.delete(`${API}/dashboard/dismiss/${encodeURIComponent(key)}`);
      setDismissed((d) => (d || []).filter((r) => r.key !== key));
      await load();
    } catch { setError('Could not bring that back.'); }
  };

  const dismissCadence = async (key, occurrence) => {
    setDismissing(key);
    try {
      await axios.post(`${API}/cadence/commitments/${key}/dismiss`,
        { occurrence_date: occurrence });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not dismiss that occurrence.');
    }
    setDismissing('');
  };

  const att = data?.attention || [];
  const zones = data?.zones || { verdict: null, do_now: [], everything_else: att };
  const m = data?.machine || {};

  const renderItem = (it) => (it.type === 'cadence_thin' ? (
    <ThinCadenceRow key={it.key} it={it} go={go} />
  ) : it.type === 'cadence' ? (
    <CadenceCard key={it.key} it={it} busy={dismissing === it.cadence?.commitment_key}
      onOpen={() => go(it.module, { tab: it.tab })} onDismiss={dismissCadence} />
  ) : (
    <AttentionRow key={it.key} it={it} go={go} onDismiss={dismissItem} onPerform={performItem}
      busy={rowBusy === it.key} done={doneMsg[it.key]} />
  ));
  // Cadence rows keep their own cards; they still get the same three dismissals beside them.
  const withDismiss = (it) => (it.type === 'cadence' || it.type === 'cadence_thin' ? (
    <div key={it.key} className="flex items-start gap-2">
      <div className="flex-1 min-w-0">{renderItem(it)}</div>
      <div className="pt-2"><DismissMenu it={it} onDismiss={dismissItem} busy={rowBusy === it.key} /></div>
    </div>
  ) : renderItem(it));
  const doors = m.leads_week || {};
  const funnel = m.funnel || {};
  const leadsWeekTotal = ['meta_forms', 'messenger', 'website', 'referral', 'warm_list', 'other']
    .reduce((s, k) => s + (doors[k] || 0), 0);

  return (
    <div className="space-y-6" data-testid="hub-dashboard">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl text-hg-text tracking-wider">DASHBOARD</h1>
          <p className="text-sm text-hg-text-dim font-sans mt-1">What needs you, and how the machine is running.</p>
        </div>
        <button onClick={load} disabled={loading}
          className="inline-flex items-center gap-1 text-[11px] font-sans text-hg-text-dim hover:text-hg-green disabled:opacity-40">
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Refresh
        </button>
      </div>

      {error && <div className="text-xs text-red-400 font-sans">{error}</div>}
      {loading && !data && <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-hg-green" /></div>}

      {data && (
        <>
          {/* ZONE 1 — NEEDS YOUR ATTENTION: verdict slot, Do now (≤3), everything else */}
          <div className="space-y-3">
            {/* Stage 2 writes the verdict here. Empty on purpose until then. */}
            <section data-testid="verdict-slot" aria-label="Verdict">{zones.verdict || null}</section>

            <div className="text-[11px] font-sans uppercase tracking-wider text-amber-300 inline-flex items-center gap-1.5">
              <AlertTriangle size={12} /> Do now
            </div>
            {att.length === 0 ? (
              <div className="bg-hg-surface border border-hg-border rounded-sm p-6 text-center" data-testid="attention-empty">
                <CheckCircle2 size={20} className="text-hg-green mx-auto mb-2" />
                <div className="text-sm font-sans text-hg-text">Nothing needs you — the machine is running.</div>
              </div>
            ) : zones.do_now.length === 0 ? (
              <div className="text-[12px] font-sans text-hg-text-dim" data-testid="do-now-empty">
                Nothing urgent — nothing late, due within a day, or waiting on you.
              </div>
            ) : (
              <div className="space-y-1.5" data-testid="do-now">{zones.do_now.map(withDismiss)}</div>
            )}

            {zones.everything_else.length > 0 && (
              <div data-testid="everything-else">
                <button onClick={() => setShowRest((v) => !v)} aria-expanded={showRest}
                  data-testid="everything-else-toggle"
                  className="w-full text-left text-[12px] font-sans text-hg-text-dim hover:text-hg-text py-1">
                  {showRest ? '▾' : '▸'} Everything else · {zones.everything_else.length} item{zones.everything_else.length === 1 ? '' : 's'}
                </button>
                {showRest && (
                  <div className="space-y-1.5 mt-1.5" data-testid="attention-list">{zones.everything_else.map(withDismiss)}</div>
                )}
              </div>
            )}

            {(data.dismissed_count || 0) > 0 && (
              <div className="text-[11px] font-sans text-hg-text-dim" data-testid="dismissed-line">
                {dismissed === null ? (
                  <button onClick={showDismissed} className="hover:text-hg-text" data-testid="dismissed-show">
                    {data.dismissed_count} dismissed · show
                  </button>
                ) : (
                  <div className="space-y-1" data-testid="dismissed-list">
                    {dismissed.map((r) => (
                      <div key={r.key} className="flex items-center gap-2">
                        <span className="flex-1 min-w-0 truncate">{r.title} · {DISMISS_MODES.find((d) => d.mode === r.mode)?.hint || r.mode}</span>
                        <button onClick={() => bringBack(r.key)} data-testid={`bring-back-${r.key}`}
                          className="shrink-0 border border-hg-border rounded-sm px-1.5 py-0.5 hover:text-hg-text">Bring back</button>
                      </div>
                    ))}
                    <button onClick={() => setDismissed(null)} className="hover:text-hg-text">hide</button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ZONE 2 — THE MACHINE AT A GLANCE */}
          <div className="space-y-2">
            <div className="text-[11px] font-sans uppercase tracking-wider text-hg-text-dim inline-flex items-center gap-1.5">
              <Activity size={12} className="text-hg-green" /> The machine at a glance
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">

              <Tile title="Leads this week" icon={Users} onClick={() => go('leads-tracker')} testid="tile-leads">
                <div className="text-2xl font-sans font-bold text-hg-text">{leadsWeekTotal}</div>
                <div className="text-[11px] font-sans text-hg-text-dim mt-1">
                  Meta {doors.meta_forms || 0} · Messenger {doors.messenger || 0} · Website {doors.website || 0}
                  {' '}· Referral {doors.referral || 0} · Warm {doors.warm_list || 0}
                </div>
                <div className="text-[11px] font-sans text-hg-text-dim mt-2">
                  Funnel: <span className="text-hg-text">{funnel.new || 0} new</span> → {funnel.contacted || 0} contacted
                  → {funnel.booked || 0} booked → <span className="text-hg-green">{funnel.won || 0} won</span>
                </div>
              </Tile>

              <Tile title="Outreach" icon={Send} onClick={() => go('drip-schedule')} testid="tile-outreach">
                <div className="text-2xl font-sans font-bold text-hg-text">{m.drip?.active ?? 0}<span className="text-sm font-normal text-hg-text-dim"> in drip</span></div>
                <div className="text-[11px] font-sans text-hg-text-dim mt-1">
                  {m.drip?.next_batch ? <>Next batch: <span className="text-hg-green">{m.drip.next_batch}</span></> : 'No batch scheduled'}
                  {m.drip?.dormant ? ` · ${m.drip.dormant} dormant` : ''}
                </div>
                <div className="text-[11px] font-sans text-hg-text-dim mt-2">
                  Letters: {m.letters?.sent ?? 0} sent · {m.letters?.replies ?? 0} replies
                  {m.letters?.staged ? <span className="text-amber-300"> · {m.letters.staged} staged</span> : ''}
                </div>
              </Tile>

              <Tile title="Campaigns" icon={Megaphone} onClick={() => go('promote')} testid="tile-campaigns">
                <div className="text-2xl font-sans font-bold text-hg-text">{(m.campaigns || []).length}<span className="text-sm font-normal text-hg-text-dim"> active</span></div>
                <div className="mt-1 space-y-0.5">
                  {(m.campaigns || []).slice(0, 3).map((c, i) => (
                    <div key={i} className="text-[11px] font-sans text-hg-text-dim truncate">
                      {c.fitter}: {c.leads} lead{c.leads === 1 ? '' : 's'}
                      {c.spend != null && <> · ${c.spend}{c.cost_per_lead != null && ` · $${c.cost_per_lead}/lead`}</>}
                    </div>
                  ))}
                  {(m.campaigns || []).length === 0 && <div className="text-[11px] font-sans text-hg-text-dim">None running</div>}
                </div>
              </Tile>

              <Tile title="Reviews" icon={Star} onClick={() => go('reviews')} testid="tile-reviews">
                <div className="text-2xl font-sans font-bold text-hg-text">
                  {m.reviews?.average ?? '—'}<span className="text-sm font-normal text-hg-text-dim"> avg ★</span>
                </div>
                <div className="text-[11px] font-sans text-hg-text-dim mt-1">
                  {m.reviews?.approved ?? 0} approved · {m.reviews?.new_this_week ?? 0} new this week
                </div>
              </Tile>

              <Tile title="Returns & Watchlist" icon={PackageOpen} onClick={() => go('returns')} testid="tile-returns">
                <div className="text-2xl font-sans font-bold text-hg-text">
                  {m.returns_watchlist?.returns_active ?? 0}<span className="text-sm font-normal text-hg-text-dim"> active returns</span>
                </div>
                <div className="text-[11px] font-sans text-hg-text-dim mt-1">
                  {m.returns_watchlist?.next_return ? `Next: ${m.returns_watchlist.next_return}` : 'No open returns'}
                </div>
                <div className="text-[11px] font-sans text-hg-text-dim mt-2"
                  onClick={(e) => { e.stopPropagation(); go('watchlist'); }} role="link">
                  Watchlist: {m.returns_watchlist?.watchlist ?? 0} entries
                  {m.returns_watchlist?.next_nudge && ` · next nudge ${m.returns_watchlist.next_nudge}`}
                </div>
              </Tile>

              <Tile title="Next 7 days" icon={CalendarDays} onClick={() => go('calendar')} testid="tile-calendar">
                {cal.length === 0 ? (
                  <div className="text-[11px] font-sans text-hg-text-dim">Nothing scheduled this week</div>
                ) : (
                  <div className="space-y-0.5">
                    {cal.slice(0, 5).map((e, i) => (
                      <div key={i} className="text-[11px] font-sans text-hg-text-dim truncate">
                        <span className="text-hg-text">{fmtWhen(e.date)}</span> · {e.title}
                      </div>
                    ))}
                    {cal.length > 5 && <div className="text-[10px] font-sans text-hg-text-dim/60">+{cal.length - 5} more</div>}
                  </div>
                )}
              </Tile>
            </div>

            {/* Recent activity */}
            <div className="bg-hg-surface border border-hg-border rounded-sm p-4">
              <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim mb-2 inline-flex items-center gap-1.5">
                <Home size={12} className="text-hg-green" /> Recent activity
              </div>
              {(m.activity || []).length === 0 ? (
                <div className="text-[11px] font-sans text-hg-text-dim">Quiet so far.</div>
              ) : (
                <div className="space-y-1">
                  {(m.activity || []).map((a, i) => (
                    <div key={i} className="text-[12px] font-sans text-hg-text-dim">
                      <span className="text-hg-text-dim/60">{fmtWhen(a.at)}</span>{' '}
                      <span className="text-hg-text/90">{a.text}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
